/**
 * `BuiltInComparable` represents all primitive or commonly comparable types in TypeScript.
 * 
 * This type can be used in contexts where a value is expected to support comparison operations,
 * such as sorting, equality checks, or relational comparisons.
 *
 * Allowed types:
 * - `number` — numeric values.
 * - `string` — textual values.
 * - `bigint` — arbitrary-precision integers.
 * - `boolean` — true/false values.
 * - `Date` — JavaScript Date objects for chronological comparison.
 */
type BuiltInComparable = number | string | bigint | boolean | Date;


/**
 * Performs a binary search on a sorted array to find the index of a given value.
 * 
 * If the value is found, the index of the value in the array is returned.
 * If the value is not found, the function returns the index where the value
 * should be inserted to maintain the array's sorted order.
 *
 * @template T - The type of elements in the sorted array. Elements must be comparable with `<` and `>`.
 * @param {T} value - The value to search for in the array.
 * @param {T[]} sortedList - A sorted array of elements.
 * @returns {number} The index of the found value, or the insertion index if the value is not found.
 *
 * @example
 * ```ts
 * const arr = [1, 3, 5, 7, 9];
 * console.log(binarySearch(5, arr)); // Output: 2
 * console.log(binarySearch(6, arr)); // Output: 3 (insertion index)
 * ```
 */
function binarySearch<T extends BuiltInComparable>(value: T, sortedList: T[]){
  let a: number = 0;
  let b: number = sortedList.length - 1;

  while (a <= b) {
    const c = ((a + b) / 2) | 0;
    const cVal = sortedList[c];

    if (cVal < value) {
      a = c + 1;
    } else if (cVal > value) {
      b = c - 1;
    } else {
      return c; 
    }

  }

  return a;
}


export default binarySearch;